import { z } from 'zod';

import { propertyAttributesUpdateSchema } from './buildings';
import { closeCaseSchema, newCaseEventSchema, newCaseSchema } from './cases';
import { fireSafetyNoteSchema, newNoteSchema } from './notes';
import {
  deletePcfraDraftInputSchema,
  deletePcfraReferrerInputSchema,
  pcfraDraftSchema,
  pcfraReferrerSchema,
  revokePcfraInputSchema,
} from './pcfra';
import {
  endHouseholdMemberSchema,
  householdMemberUpdateSchema,
  personContactDetailsUpdateSchema,
  tenantUpdateSchema,
  updatePcfraStatusSchema,
  updatePcfraReviewDateSchema,
  createHouseholdMemberSchema,
  updatePcfraSelfReferredSchema,
  updateRpeepStatusSchema,
} from './people';
import { visitSchema, visitUpdateSchema } from './visits';

export enum UserMessageStatus {
  /** The message is awaiting another message that must be processed first */
  AWAITING_DEPENDENCY = 'awaiting-dependency',
  /** The message has just been dispatched */
  PENDING = 'pending',
  /** Server has accepted the message */
  ENQUEUED = 'enqueued',
  /** Server has completed processing the message */
  COMPLETED = 'completed',
  /** Skipped: a newer write to the same record was already applied. Terminal, not an error */
  SUPERSEDED = 'superseded',
  /** Server failed to process the message */
  FAILED = 'failed',
}

export const messagePayloadSchemas = {
  updatePropertyAttributes: propertyAttributesUpdateSchema,
  logVisit: visitSchema,
  editVisitLog: visitUpdateSchema,
  deleteVisitLog: visitSchema.pick({ visitId: true }),
  updateTenant: tenantUpdateSchema,
  updateHouseholdMember: householdMemberUpdateSchema,
  createHouseholdMember: createHouseholdMemberSchema,
  endHouseholdMember: endHouseholdMemberSchema,
  updateContactDetails: personContactDetailsUpdateSchema,
  createCase: newCaseSchema,
  createCaseEvent: newCaseEventSchema,
  closeCase: closeCaseSchema,
  createNote: newNoteSchema,
  updatePcfraStatus: updatePcfraStatusSchema,
  updateRpeepStatus: updateRpeepStatusSchema,
  updatePcfraReviewDate: updatePcfraReviewDateSchema,
  updatePcfraSelfReferred: updatePcfraSelfReferredSchema,
  updatePcfraReferrer: pcfraReferrerSchema,
  deletePcfraReferrer: deletePcfraReferrerInputSchema,
  savePcfraDraft: pcfraDraftSchema,
  deletePcfraDraft: deletePcfraDraftInputSchema,
  revokePcfra: revokePcfraInputSchema,
  revokeRpeep: revokePcfraInputSchema,
  createFireSafetyNote: fireSafetyNoteSchema,
};

/** Source of truth for the `messages` user store table (see userStoreSchema.ts) */
export const userMessageSchema = z.object({
  messageId: z.string(),
  /**
   * Client creation time, ordering writes to the same record. UTC only — the comparisons are
   * made between raw strings, which only sort chronologically under one offset
   */
  timestamp: z.iso.datetime(),
  /** One of the known message types; an unrecognised type fails schema validation */
  type: z.object(messagePayloadSchemas).keyof(),
  status: z.enum(UserMessageStatus),
  entityId: z.string(),
  newEntityTempId: z.string(),
  dependencyId: z.string(),
  error: z.string(),
  /**
   * Native object cell. Payloads are validated per message type by
   * `messagePayloadSchemas[type].parse` at dispatch (UserMessageDispatch.ts)
   */
  payload: z.record(z.string(), z.unknown()),
  wasCreatedOffline: z.boolean(),
});

export const enqueuedUserMessageSchema = z.object({
  message: userMessageSchema,
  messageId: z.string(),
  entityId: z.string(),
  dependencyId: z.string().optional(),
  userId: z.string(),
});
